import { mergeWith, pick } from 'lodash';
import { useCallback } from 'react';

import { type ShareUrlUpdatedSetting } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';

import { type ShareUrlResponseWithSource } from '~/components/SharePrivateClipModal/types';
import { useRevalidatePublicBoard } from '~/hooks/useRevalidatePublicBoard';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { replacePresentationSubObjectsMergeCustomizer } from '~/swr-hooks/shortUrl/replacePresentationSubObjectsMergeCustomizer';
import { type ShareUrlUpdateHandlerProps, useUpdateShortId } from '~/swr-hooks/shortUrl/useUpdateShortId';

/** Fields allowed on `ShortUrl.update` for boards — identity/list metadata is excluded. */
const UPDATABLE_BOARD_SHORT_URL_FIELDS = [
  'passcode',
  'disabled',
  'requireAccount',
  'expirationDate',
  'permissions',
  'presentationSettings',
  'presentationContent',
  'presentationPages',
  'boardContext',
] as const;

type UseUpdateShareBoardLinkParams = {
  boardId: string;
  shortIdInfo: ShortUrlResponse;
};

export const useUpdateShareBoardLink = ({ boardId, shortIdInfo }: UseUpdateShareBoardLinkParams) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { updateShortId } = useUpdateShortId();
  const { revalidatePublicBoard } = useRevalidatePublicBoard();

  const onShortUrlUpdate = useCallback(
    async ({
      props,
      analytics,
    }: {
      props: ShareUrlUpdateHandlerProps;
      analytics?: ShareUrlUpdatedSetting;
    }): Promise<ShareUrlResponseWithSource> => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      const response = await updateShortId.mutateAsync({
        objectId: boardId,
        boardContext: shortIdInfo.boardContext,
        updatePayload: {
          workspaceId,
          ...pick(
            mergeWith({}, shortIdInfo, props, replacePresentationSubObjectsMergeCustomizer),
            UPDATABLE_BOARD_SHORT_URL_FIELDS,
          ),
          shortId: shortIdInfo.id,
        },
        analyticsPayload: analytics ? { objectType: 'board', setting: analytics } : undefined,
      });

      /**
       * Don't await since this can be slow
       * @see https://air-labs-team.slack.com/archives/CSU3B8B3R/p1699565392614499?thread_ts=1699564602.295969&cid=CSU3B8B3R
       */
      revalidatePublicBoard(boardId);

      return {
        ...response,
        objectId: boardId,
        objectType: 'board',
      };
    },
    [boardId, currentWorkspace?.id, revalidatePublicBoard, shortIdInfo, updateShortId],
  );

  return {
    onShortUrlUpdate,
    isLoading: updateShortId.isPending,
  };
};
